import {DocumentIcon, XMarkIcon} from '@heroicons/react/24/outline';
import {I18n} from '@lingui/core';
import {createTRPCReact} from '@trpc/react-query';
import {ServerRouter} from '@zentact/api';
import {
  getDefenseDocumentTypeCode,
  getDefenseReasonCode,
  supportedDefenseDocumentExtensions,
} from '@zentact/common';
import {useMemo, useState} from 'react';
import {Typography} from '../..';
import {
  AlertOverlayWithConfirmation,
  Button,
  FileUpload,
  InputRadio,
  Loading,
  TruncatedText,
  megabytesToBytes,
  useNotification,
} from '../../..';

type Props = {
  trpc: ReturnType<typeof createTRPCReact<ServerRouter>>;
  disputePspReference: string;
  i18n: I18n;
  onDefenseSubmitted: () => void;
};

type DefenseDocumentType = {
  defenseDocumentTypeCode: string;
  available: boolean;
  requirementLevel: string;
};

type DefenceReason = {
  defenseReasonCode: string;
  satisfied: boolean;
  defenseDocumentTypes?: DefenseDocumentType[] | undefined;
};

type UploadedFile = {
  name: string;
  content: string;
  contentType: string;
  fileSize: number;
};

export const DisputeApiDetails = ({trpc, disputePspReference, i18n, onDefenseSubmitted}: Props) => {
  const [defendConfirmationOpen, setDefendConfirmationOpen] = useState(false);

  const [uploadedFiles, setUploadedFiles] = useState<Record<string, UploadedFile[]>>({}); // files array by defenseDocumentTypeCode

  const defenseReasons = trpc.chargeback.getDefenseReasons.useQuery(
    {
      disputePspReference,
    },
    {
      onSuccess(data) {
        if (!defenseReasonState.defenseReasonCode) {
          return;
        }

        const activeDefenseReason = data.defenseReasons?.find(
          row => row.defenseReasonCode === defenseReasonState.defenseReasonCode
        );

        if (activeDefenseReason) {
          setDefenseReasonState(activeDefenseReason);
        }
      },
    }
  );
  const [defenseReasonState, setDefenseReasonState] = useState<DefenceReason>({
    defenseReasonCode: '',
    satisfied: false,
    defenseDocumentTypes: undefined,
  });
  const {showSuccessNotification, showErrorNotification} = useNotification();
  const defendDispute = trpc.chargeback.defendDispute.useMutation({
    onSuccess: () => {
      showSuccessNotification(
        i18n._('Defend Dispute'),
        i18n._('You have successfully submitted a dispute defense')
      );
      defenseReasons.refetch();
      onDefenseSubmitted();
    },
    onError: error => {
      showErrorNotification(i18n._('Error'), error.message);
    },
  });

  const supplyDocumentsMutation = trpc.chargeback.supplyDefenseDocument.useMutation({
    onError: error => {
      showErrorNotification(i18n._('Error'), error.message);
    },
  });

  const onSelectFile = async (file: File, document: DefenseDocumentType) => {
    const fileName = file.name;
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = async () => {
      // The result is a raw base64 string, stripped of the data URL scheme, MIME type, and encoding indicator
      const base64String = (reader.result as string).replace('data:', '').replace(/^.+,/, '');
      setUploadedFiles(prev => ({
        ...prev,
        [document.defenseDocumentTypeCode]: [
          ...(prev[document.defenseDocumentTypeCode] || []),
          {name: fileName, content: base64String, contentType: file.type, fileSize: file.size},
        ],
      }));
      showSuccessNotification(
        i18n._('Document supplied'),
        i18n._('Your defense document has been successfully submitted')
      );
    };
    reader.onerror = error => {
      console.error('Error reading file:', error);
    };
  };

  const onDefendSubmit = async () => {
    setDefendConfirmationOpen(false);

    // upload files in batches by defenseDocumentTypeCode
    await Promise.all(
      Object.entries(uploadedFiles)
        .filter(([defenseDocumentTypeCode]) =>
          defenseReasonState.defenseDocumentTypes?.find(
            // clean up doc types of other reason codes
            docType => docType.defenseDocumentTypeCode === defenseDocumentTypeCode
          )
        )
        .map(([defenseDocumentTypeCode, files]) =>
          supplyDocumentsMutation.mutateAsync({
            defenseDocumentTypeCode,
            disputePspReference,
            defenseDocuments: files,
          })
        )
    );

    defendDispute.mutate({
      defenseReasonCode: defenseReasonState.defenseReasonCode,
      disputePspReference,
    });
  };

  const deleteDocument = (fileIndex: number, defenseDocumentTypeCode: string) => {
    setUploadedFiles(prev => ({
      ...prev,
      [defenseDocumentTypeCode]:
        prev[defenseDocumentTypeCode]?.filter((_, index) => index !== fileIndex) ?? [],
    }));
  };
  const isEnoughFilesUploaded = useMemo(() => {
    if (!defenseReasonState.defenseDocumentTypes) {
      return false;
    }
    for (const docType of defenseReasonState.defenseDocumentTypes) {
      if (!uploadedFiles[docType.defenseDocumentTypeCode]?.length) {
        return false;
      }
    }
    return true;
  }, [defenseReasonState, uploadedFiles]);

  if (defenseReasons.isLoading || !defenseReasons.data) {
    return <Loading />;
  }

  return (
    <>
      <Typography variant="header-section" className="mb-4">
        {i18n._('Possible Defense Reasons')}
      </Typography>
      <div className="">
        <div>
          {defenseReasons.data?.defenseReasons?.map(reason => {
            const defenseReasonCode = getDefenseReasonCode(reason.defenseReasonCode, i18n);
            if (!defenseReasonCode) {
              console.error('Unknown defense reason code: ', reason.defenseReasonCode);
            }
            return (
              <div key={reason.defenseReasonCode} className="pb-6 sm:grid sm:gap-4 ">
                <InputRadio
                  label={
                    <div className="mx-1">
                      {defenseReasonCode ||
                        i18n._('Defense reason code: {reasonCode}', {
                          reasonCode: reason.defenseReasonCode,
                        })}
                    </div>
                  }
                  inputClassName="mt-1 w-4 h-4 bg-gray-100 border-gray-300 cursor-pointer text-primary-600 focus:ring-primary-500 disabled:opacity-70"
                  wrapperClassName="flex items-start space-x-2"
                  value={reason.defenseReasonCode}
                  name="defenseReason"
                  onClick={() => setDefenseReasonState(reason)}
                />
              </div>
            );
          })}
        </div>
        <div>
          {defenseReasonState.defenseReasonCode && (
            <>
              <Typography variant="header-section" className="my-4">
                {i18n._('Defense Documents')}
              </Typography>
              {defenseReasonState.defenseDocumentTypes?.map(document => (
                <div key={document.defenseDocumentTypeCode} className="my-4">
                  {getDefenseDocumentTypeCode(document.defenseDocumentTypeCode, i18n)}
                  <div className="flex flex-col gap-2 my-2">
                    {uploadedFiles[document.defenseDocumentTypeCode]?.map((file, index) => (
                      <div key={`${document.defenseDocumentTypeCode}-${index}`}>
                        <div className="flex items-center justify-between w-full my-2">
                          <div className="flex items-center space-x-2">
                            <DocumentIcon className="w-6 h-6 mr-1" />
                            <TruncatedText text={file.name} />
                          </div>
                          <button
                            type="button"
                            onClick={() => deleteDocument(index, document.defenseDocumentTypeCode)}
                          >
                            <XMarkIcon className="w-6 h-6" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="my-2">
                    <FileUpload
                      onSelectFile={f => onSelectFile(f, document)}
                      allowedExt={supportedDefenseDocumentExtensions}
                      localeText={{
                        upload: '',
                        dragndrop: '',
                      }}
                      maxSizeMb={8}
                      customFileValidation={({fileSize, extension}) => {
                        const maxBodySizeLimitByDefenseDocumentType = 8; // adyen request mb limit
                        const fileSizeLimit = extension === 'pdf' ? 2 : 8;
                        if (fileSize > megabytesToBytes(fileSizeLimit)) {
                          return {
                            result: false,
                            message: i18n._(
                              'The uploaded .{extension} file is larger than {size}MB',
                              {
                                size: fileSizeLimit,
                                extension,
                              }
                            ),
                          };
                        }
                        const currentTotalFileSizeByType =
                          uploadedFiles[document.defenseDocumentTypeCode]?.reduce(
                            (acc, d) => acc + d.fileSize,
                            0
                          ) ?? 0;
                        if (
                          currentTotalFileSizeByType + fileSize >
                          megabytesToBytes(maxBodySizeLimitByDefenseDocumentType)
                        ) {
                          return {
                            result: false,
                            message: i18n._(
                              'The total uploaded documents size of the selected type is larger than {size}MB',
                              {
                                size: maxBodySizeLimitByDefenseDocumentType,
                              }
                            ),
                          };
                        }

                        return {result: true};
                      }}
                    />
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
        <div className="flex justify-end">
          <Button
            variant="primary"
            size="lg"
            className="my-2 w-fit"
            disabled={
              !isEnoughFilesUploaded || defendDispute.isLoading || supplyDocumentsMutation.isLoading
            }
            isLoading={defendDispute.isLoading || supplyDocumentsMutation.isLoading}
            onClick={() => setDefendConfirmationOpen(true)}
          >
            {i18n._('Defend Dispute')}
          </Button>
        </div>
      </div>
      <AlertOverlayWithConfirmation
        setOpen={() => setDefendConfirmationOpen(false)}
        handleAction={onDefendSubmit}
        localeText={{
          title: i18n._('Confirm dispute defense'),
          description: i18n._(
            `Are you sure you want to submit the selected defense documents? Once submitted, you won't be able to update them or upload new documents.`
          ),
          cancel: i18n._('Cancel'),
          confirm: i18n._('Submit Defense'),
        }}
        loading={defendDispute.isLoading || supplyDocumentsMutation.isLoading}
        mode="success"
        open={Boolean(defendConfirmationOpen)}
      />
    </>
  );
};
